<template>
<div class="login-form-container">
    <form class="login-form" novalidate @keyup.enter="login">
        <md-input-container :class="{'md-input-invalid': emailError() || userNotFoundError()}">
            <label for="email-input">Email Address</label>
            <md-input 
                id="email-input"
                v-model="email" 
                type="email" 
                required
                @blur="validateEmail"
            ></md-input>
            <span class="md-error" v-if="emailError()">
                {{errorMessage}} 
                <a v-if="verifyError && !sent && email" 
                   class="send-verification-email" 
                   @click="sendVerficationEmail"
                >
                    Send Verification Email
                </a>
            </span>
        </md-input-container>

        <div class="error-user-not-found" v-if="userNotFoundError()">
            <md-icon class="error-icon">error_outline</md-icon>
            <span>The email address you have entered is not currently in our system. If you are a new user, please click on the 'Create Account' button below.</span>
        </div>

        <md-input-container :class="{'md-input-invalid': passwordError()}">
            <label for="password-input">Password</label>
            <md-input 
                id="password-input"
                :type="showCurrentPassword ? 'text' : 'password'" 
                v-model="password" 
                required
            ></md-input>
            <span class="toggle-password" @click="toggleShowPassword('current')">
                <md-icon>{{ showCurrentPassword ? 'visibility' : 'visibility_off' }}</md-icon>
            </span>
            <span class="md-error" v-if="passwordError">{{errorMessage}}</span>
        </md-input-container>

        <p class="signin-error" role="alert" v-if="generalErrorMessage">{{ generalErrorMessage }}</p>

        <md-button 
            class="md-raised md-primary login-button" 
            @click="login"
            :disabled="loading || !isFormValid"
        >
            <md-spinner v-if="loading" :md-size="20" class="md-accent"></md-spinner>
            <span v-else>Sign In</span>
        </md-button>

        <div class="links-below">
            <div class="forgot-password-link">
                <md-button 
                    @click="forgot"
                    :disabled="loading"
                    flat
                >
                    Forgot Password?
                </md-button>
            </div>
            <div class="register-link">
                <span>Don't have an account?</span>
                <md-button 
                    class="create-account-link" 
                    @click="toggleRegister"
                    flat
                >
                    Create Account
                </md-button>
            </div>
        </div>
    </form>
    <md-dialog-confirm
        :md-title="confirm.title"
        :md-content-html="getConfirmContent()"
        :md-ok-text="confirm.ok"
        :md-cancel-text="confirm.cancel"
        @close="onDialogClose"
        ref="loginDialog">
    </md-dialog-confirm>
</div>
</template>

<script>
import UAParser from 'ua-parser-js';
import firebase from 'firebase/app';
import { db } from '@/main';
import signinErrorMessage, { fieldErrorMessage, isPasswordFieldError } from '@/lib/signin-error-message';

function UnverifiedException() {
    this.code = 'auth/unverified-email';
    this.message = 'This email is not confirmed yet.';
}

const LoginForm = {
    name: 'LoginForm',
    data() {
        return {
            email: '',
            password: '',
            errorCode: '',
            errorMessage: '',
            sent: false,
            sessionID: '',
            deviceInfo: null,
            sessions: {},
            reusingSession: false,
            userRef: null,
            confirm: {
                title: 'Device Limit Reached',
                ok: 'Continue',
                cancel: 'Cancel'
            },
            loading: false,
            showCurrentPassword: false,
            userProjectIds: []
        };
    },
    computed: {
        // Errors that aren't about one field (no connection, too many attempts, ...) used to show nowhere.
        generalErrorMessage() {
            return this.errorCode ? signinErrorMessage(this.errorCode) : '';
        },
        isFormValid() {
            return this.email && this.password;
        }
    },
    methods: {
        validateEmail() {
            if (!this.email) {
                this.message = 'Email is required';
                return false;
            }
            this.message = '';
            return true;
        },
        toggleShowPassword(field) {
            if (field === 'current') {
                this.showCurrentPassword = !this.showCurrentPassword;
            }
        },
        getConfirmContent() {
            return 'You are currently logged in on 3 devices. Signing in here will log you out from another device. Do you want to continue?';
        },
        getDeviceType(uaDeviceType) {
            if (uaDeviceType === 'mobile') return 'Mobile';
            if (uaDeviceType === 'tablet') return 'Tablet';
            return 'Desktop';
        },
        async getDeviceInfo() {
            const parser = new UAParser();
            const browserInfo = parser.getBrowser();
            const osInfo = parser.getOS();
            const device = parser.getDevice();
            const deviceType = this.getDeviceType(device.type);
            const deviceVendorModel = device.vendor ? `${device.vendor} (${device.model})` : '';

            let ip = 'Unknown';

            try {
                const ipResponse = await fetch('https://1.1.1.1/cdn-cgi/trace');
                ip = (await ipResponse.text()).match(/ip=([\d.]+)/)[1];
            } catch (error) {
                console.error('Failed to fetch IP:', error);
            }
            const loginTime = new Date().toISOString();

            return {
                deviceType,
                deviceVendorModel,
                browser: browserInfo.name && browserInfo.version ? `${browserInfo.name} (${browserInfo.version})` : browserInfo.name || 'Unknown',
                os: osInfo.name && osInfo.version ? `${osInfo.name} (${osInfo.version})` : osInfo.name || 'Unknown',
                ip,
                loginTime
            };
        },
        forgot() {
            this.$router.replace('/forgot');
        },
        async login() {
            if (!this.isFormValid) return;

            try {
                this.loading = true;
                this.errorCode = '';
                this.$emit('attempt');
                this.$store.dispatch('setLoaderLogin', { type: true });

                const { user } = await firebase.auth().signInWithEmailAndPassword(this.email, this.password);

                if (!location.host.startsWith('localhost') && !user.emailVerified) {
                    this.sent = false;
                    throw new UnverifiedException();
                }

                this.deviceInfo = await this.getDeviceInfo();

                this.userRef = db.collection('users').doc(user.uid);
                const userDoc = await this.userRef.get();
                const userData = userDoc.data();
                const userDeviceSessionData = userData.userDeviceSession || {};
                this.sessions = userDeviceSessionData;

                // Reclaim this browser's existing slot instead of taking another.
                //
                // A slot is only released by an explicit Log Out. Closing the tab,
                // quitting the browser, or letting the session lapse leaves the
                // entry behind — so before this, signing in again from the SAME
                // machine minted a fresh random id and consumed a second slot, then
                // a third, and the fourth sign-in evicted the user's own oldest
                // session. People were competing with their own ghosts, and the
                // browser/OS/IP stored alongside was only ever display metadata —
                // nothing compared it.
                //
                // localStorage is the only durable per-browser handle available.
                // Deliberately NOT falling back to matching on browser + OS + IP:
                // that would merge two genuinely different machines behind one
                // slot, which is a worse failure than taking an extra one. A
                // private window or cleared storage still gets a new slot, and
                // that is correct — there is no way to know otherwise.
                const storedSessionID = localStorage.getItem('sessionID');
                this.reusingSession = !!storedSessionID
                    && Object.prototype.hasOwnProperty.call(userDeviceSessionData, storedSessionID);

                if (this.reusingSession) {
                    this.sessionID = storedSessionID;
                } else {
                    const array = new Uint8Array(32);
                    window.crypto.getRandomValues(array);
                    this.sessionID = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
                }

                const userProjectIds = Object.keys(userData.projects);
                this.userProjectIds = userProjectIds;

                const sessionArray = Object.entries(userDeviceSessionData)
                    .map(([id, info]) => ({ id, ...info }))
                    .sort((a, b) => new Date(a.loginTime) - new Date(b.loginTime));

                // Only warn when this sign-in would actually take a slot. Reusing
                // one leaves the count unchanged, so the prompt would be a lie.
                if (!this.reusingSession && sessionArray.length >= 3) {
                    this.$store.dispatch('setLoaderLogin', { type: false });
                    this.$refs.loginDialog.open();
                } else {
                    await this.saveSession();
                }
            } catch (error) {
                // A failure with no code (a read after sign-in) still gets the general line.
                this.errorCode = (error && error.code) || 'unknown';
                this.errorMessage = this.getErrorMessage(error.code);
                this.loading = false;
                this.$store.dispatch('setLoaderLogin', { type: false });
            }
        },
        getErrorMessage(errorCode) {
            return fieldErrorMessage(errorCode);
        },
        async saveSession() {
            const sessionArray = Object.entries(this.sessions)
                .map(([id, info]) => ({ id, ...info }))
                .sort((a, b) => new Date(a.loginTime) - new Date(b.loginTime));

            const sessionUpdate = { ...this.sessions };

            // Reusing an existing slot does not grow the count, so nothing is
            // evicted — it only refreshes this entry's device info and loginTime.
            if (!this.reusingSession && sessionArray.length >= 3) {
                const oldestSession = sessionArray[0];
                delete sessionUpdate[oldestSession.id];

                await this.userRef.update({
                    [`userDeviceSession.${oldestSession.id}`]: firebase.firestore.FieldValue.delete()
                });
            }

            sessionUpdate[this.sessionID] = this.deviceInfo;

            await this.userRef.update({
                userDeviceSession: sessionUpdate
            });

            await this.userProjectUpdate();

            localStorage.setItem('sessionID', this.sessionID);
            localStorage.setItem('sessionIDDateTime', new Date());
            this.$store.dispatch('setLoaderLogin', { type: false });
            this.$router.replace('/dashboard').then(async() => {
                this.loading = false;
            });
        },
        async userProjectUpdate() {
            try {
                if (this.userProjectIds.length > 0) {
                    await Promise.all(this.userProjectIds.map(async (projectId) => {
                        const projectSnapshot = await db.collection('projects').doc(projectId).get();
                        if (projectSnapshot.exists) {
                            const lastChangedDate = projectSnapshot.data().lastChangedDate;
                            if (lastChangedDate) {
                                await this.userRef.update({
                                    [`projects.${projectId}.lastChangedDate`]: lastChangedDate
                                });
                            }
                        }
                    }));
                }
            } catch (error) {
                console.error('Error in userProjectUpdate:', error);
                throw error;
            }
        },
        async onDialogClose(type) {
            if (type === 'ok') {
                this.$store.dispatch('setLoaderLogin', { type: true });
                await this.saveSession();
            }
            this.loading = false;
        },
        verifyError() {
            return this.errorCode === 'auth/unverified-email';
        },
        emailError() {
            return this.errorCode === 'auth/invalid-email' || this.errorCode === 'auth/unverified-email';
        },
        userNotFoundError() {
            return this.errorCode === 'auth/user-not-found';
        },
        passwordError() {
            return isPasswordFieldError(this.errorCode);
        },
        sendVerficationEmail() {
            // actionCodeSettings.url adds a "Continue" link back to the app on the
            // verification success page (env-correct via window.location.origin).
            firebase.auth().currentUser.sendEmailVerification({
                url: `${window.location.origin}/login`,
                handleCodeInApp: false
            }).then(() => {
                this.sent = true;
            });
        },
        toggleRegister() {
            this.$emit('toggle-register');
        }   
    }
};

export default LoginForm;
</script>

<style lang="scss" scoped>
.login-form-container {
    width: 100%;
}

.login-form {
    background: white;
    border-radius: 8px;
}

.signin-error {
    margin: 16px 0 0;
    color: #d32f2f;
    font-size: 14px;
    line-height: 1.4;
}

.login-button {
    width: 100%;
    height: 48px;
    font-size: 16px;
    text-transform: none;
    margin: 6px 0px;
}

.links-below {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
    margin-bottom: 10px;

    .forgot-password-link {
        color: #666;
        text-transform: none;
        font-size: 14px;
        background: none;
        box-shadow: none;
        min-width: unset;
        padding: 0;
        margin-bottom: 2px;
        display: flex;
        align-items: end;
        justify-content: end;
    }
    .register-link {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        font-size: 14px;
        color: #666;
        width: 100%;
        .create-account-link {
            color: #2196F3;
            text-transform: none;
            font-weight: 500;
            background: none;
            box-shadow: none;
            min-width: unset;
            padding: 0;
            &:hover {
                text-decoration: underline;
                background: none;
            }
        }
    }
}

.divider {
    display: flex;
    align-items: center;
    text-align: center;
    margin: 20px 0 24px 0;
    color: #666;
    font-size: 14px;
    &::before,
    &::after {
        content: '';
        flex: 1;
        border-bottom: 1px solid #ddd;
    }
    span {
        padding: 0 20px;
    }
}

.sso-login {
    text-align: center;
    margin-bottom: 0;
    .google-button {
        width: 100%;
        height: 48px;
        font-size: 16px;
        text-transform: none;
        background-color: #fff;
        color: #444;
        border: 1px solid #ddd;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        font-weight: 500;
        box-shadow: 0 2px 4px rgba(60,64,67,.08);
        transition: background 0.2s, box-shadow 0.2s;
        margin: 0 auto;
        &:hover {
            background-color: #f5f5f5;
            box-shadow: 0 4px 8px rgba(60,64,67,.12);
        }
        .google-icon {
            width: 24px;
            height: 24px;
            margin-right: 8px;
            background: none;
            box-shadow: none;
        }
    }
}

.toggle-password {
    cursor: pointer;
    margin-left: 10px;
    color: #666;
    font-size: 1.2em;
    display: inline-flex;
    align-items: center;
}

.error-user-not-found {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: #f44336;
    font-size: 14px;
    text-align: left;
    margin-bottom: 24px;
    margin-top: -24px;
    padding: 8px;
    background-color: #ffebee;
    border-radius: 4px;
    .error-icon {
        color: #f44336;
        font-size: 20px;
        margin-top: 2px;
    }
}

.send-verification-email {
    text-decoration: underline;
    cursor: pointer;
    color: #2196F3 !important;
    transition: color 0.15s ease;
    font-size: 14px;
    margin-left: 8px;
    &:hover {
        color: #1976D2 !important;
    }
}

.md-error {
    color: #f44336;
    font-size: 12px;
    margin-top: 5px;
}

.md-input-container {
    margin-bottom: 20px;
}

@media (max-width: 600px) {
    .login-form {
        padding: 30px 20px;
    }
}
</style>


// WEBPACK FOOTER //
// login-form.vue?86a8c67e